import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Target,
  Play,
  Pause,
  CheckCheck,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays, isAfter, isBefore } from "date-fns";

const statusConfig = {
  planned: {
    label: "Planned",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-400",
  },
  live: {
    label: "Live",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500 animate-pulse",
  },
  paused: {
    label: "Paused",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-400",
  },
  completed: {
    label: "Completed",
    badge: "bg-slate-50 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
};

const platformConfig = {
  Meta: { color: "bg-[#1877F2] text-white", short: "META" },
  Google: {
    color: "bg-white text-slate-700 border border-slate-200",
    short: "G",
  },
  LinkedIn: { color: "bg-[#0A66C2] text-white", short: "IN" },
  Twitter: { color: "bg-black text-white", short: "X" },
};

const emptyForm = {
  client_name: "",
  client_id: "",
  platform: "",
  objective: "",
  creative_type: "",
  start_date: "",
  end_date: "",
  assigned_employee: "",
};

function toDateInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 10); // "YYYY-MM-DD"
}

function campaignProgress(startDate, endDate, status) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();
  if (status === "completed") return 100;
  if (isBefore(now, start)) return 0;
  if (isAfter(now, end)) return 100;
  const total = differenceInDays(end, start) || 1;
  const elapsed = differenceInDays(now, start);
  return Math.round((elapsed / total) * 100);
}

function AdCardSkeleton() {
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="flex gap-6">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-2 w-full rounded-full mt-2" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
    </Card>
  );
}

function SummaryPill({ label, count, color }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ${color}`}
    >
      <span className="text-xl sm:text-2xl font-bold">{count}</span>
      <span className="text-xs sm:text-sm font-medium opacity-80">{label}</span>
    </div>
  );
}

/** Reusable campaign form used for both create and edit */
function CampaignForm({
  formData,
  setFormData,
  onSubmit,
  submitLabel = "Create Campaign",
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 pt-1">
      <div>
        <Label htmlFor="client_name">Client Name *</Label>
        <Input
          id="client_name"
          value={formData.client_name}
          onChange={(e) =>
            setFormData({ ...formData, client_name: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="platform">Platform *</Label>
        <select
          id="platform"
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          value={formData.platform}
          onChange={(e) =>
            setFormData({ ...formData, platform: e.target.value })
          }
          required
        >
          <option value="">Select platform</option>
          <option value="Meta">Meta (Facebook/Instagram)</option>
          <option value="Google">Google Ads</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Twitter">Twitter</option>
        </select>
      </div>
      <div>
        <Label htmlFor="objective">Objective *</Label>
        <Input
          id="objective"
          placeholder="e.g., Lead Generation, Brand Awareness"
          value={formData.objective}
          onChange={(e) =>
            setFormData({ ...formData, objective: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="creative_type">Creative Type *</Label>
        <Input
          id="creative_type"
          placeholder="e.g., Image, Video, Carousel"
          value={formData.creative_type}
          onChange={(e) =>
            setFormData({ ...formData, creative_type: e.target.value })
          }
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_date">Start Date *</Label>
          <Input
            id="start_date"
            type="date"
            value={formData.start_date}
            onChange={(e) =>
              setFormData({ ...formData, start_date: e.target.value })
            }
            required
          />
        </div>
        <div>
          <Label htmlFor="end_date">End Date *</Label>
          <Input
            id="end_date"
            type="date"
            value={formData.end_date}
            onChange={(e) =>
              setFormData({ ...formData, end_date: e.target.value })
            }
            required
          />
        </div>
      </div>

      <Button type="submit" className="w-full">
        {submitLabel}
      </Button>
    </form>
  );
}

export default function AdsPage() {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  const isPrivileged = user?.role === "owner" || user?.role === "pillar";

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await apiClient.get("/ads");
      setAds(response.data);
    } catch {
      toast.error("Failed to load ads");
    } finally {
      setLoading(false);
    }
  };

  /* ── CREATE ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...createForm,
        start_date: new Date(createForm.start_date).toISOString(),
        end_date: new Date(createForm.end_date).toISOString(),
      };
      await apiClient.post("/ads", payload);
      toast.success("Ad campaign created successfully");
      setCreateOpen(false);
      setCreateForm(emptyForm);
      fetchAds();
    } catch {
      toast.error("Failed to create ad campaign");
    }
  };

  /* ── EDIT ── */
  const openEdit = (ad) => {
    setEditingAd(ad);
    setEditForm({
      client_name: ad.client_name,
      client_id: ad.client_id ?? "",
      platform: ad.platform,
      objective: ad.objective,
      creative_type: ad.creative_type,
      start_date: toDateInput(ad.start_date),
      end_date: toDateInput(ad.end_date),
      assigned_employee: ad.assigned_employee ?? "",
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editForm,
        start_date: new Date(editForm.start_date).toISOString(),
        end_date: new Date(editForm.end_date).toISOString(),
      };
      await apiClient.patch(`/ads/${editingAd.id}`, payload);
      toast.success("Ad campaign updated successfully");
      setEditOpen(false);
      setEditingAd(null);
      fetchAds();
    } catch {
      toast.error("Failed to update ad campaign");
    }
  };

  /* ── DELETE ── */
  const handleDelete = async (adId) => {
    try {
      await apiClient.delete(`/ads/${adId}`);
      toast.success("Ad campaign deleted");
      setDeletingId(null);
      fetchAds();
    } catch {
      toast.error("Failed to delete ad campaign");
    }
  };

  /* ── STATUS ── */
  const updateStatus = async (adId, status) => {
    try {
      await apiClient.patch(`/ads/${adId}`, { status });
      toast.success("Ad status updated");
      fetchAds();
    } catch {
      toast.error("Failed to update ad");
    }
  };

  const counts = {
    planned: ads.filter((a) => a.status === "planned").length,
    live: ads.filter((a) => a.status === "live").length,
    paused: ads.filter((a) => a.status === "paused").length,
    completed: ads.filter((a) => a.status === "completed").length,
  };

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="ads-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
        <div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Ads Management
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Plan and track advertising campaigns
          </p>
        </div>

        {isPrivileged && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-ad-button"
                className="w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Plan Ad
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full">
              <DialogHeader>
                <DialogTitle>Plan Ad Campaign</DialogTitle>
                <DialogDescription>
                  Fill in the campaign details below.
                </DialogDescription>
              </DialogHeader>
              <CampaignForm
                formData={createForm}
                setFormData={setCreateForm}
                onSubmit={handleCreate}
                submitLabel="Create Campaign"
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* ── Summary pills ── */}
      {!loading && ads.length > 0 && (
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <SummaryPill
            label="Planned"
            count={counts.planned}
            color="bg-blue-50 text-blue-700"
          />
          <SummaryPill
            label="Live"
            count={counts.live}
            color="bg-emerald-50 text-emerald-700"
          />
          <SummaryPill
            label="Paused"
            count={counts.paused}
            color="bg-amber-50 text-amber-700"
          />
          <SummaryPill
            label="Completed"
            count={counts.completed}
            color="bg-slate-100 text-slate-600"
          />
        </div>
      )}

      {/* ── Ad cards ── */}
      <div className="grid gap-3 fade-in">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <AdCardSkeleton key={i} />)
        ) : ads.length === 0 ? (
          <Card className="py-16 text-center border-dashed">
            <Target className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-medium text-muted-foreground">
              No ad campaigns yet
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Plan your first campaign to get started
            </p>
          </Card>
        ) : (
          ads.map((ad) => {
            const sc = statusConfig[ad.status] ?? statusConfig.planned;
            const pc = platformConfig[ad.platform];
            const pct = campaignProgress(ad.start_date, ad.end_date, ad.status);
            const totalDays = differenceInDays(
              new Date(ad.end_date),
              new Date(ad.start_date),
            );

            return (
              <Card key={ad.id} className="p-4 sm:p-5 hover-lift">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  {/* ── Top section: icon + info + actions (mobile stacked) ── */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Platform badge */}
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${pc?.color ?? "bg-slate-100 text-slate-700"}`}
                    >
                      {pc?.short ?? ad.platform.slice(0, 2).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-base">
                          {ad.client_name}
                        </h3>
                        <Badge
                          className={`text-xs border ${sc.badge} flex items-center gap-1.5`}
                        >
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${sc.dot}`}
                          />
                          {sc.label}
                        </Badge>
                      </div>

                      {/* Meta row */}
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground mb-3">
                        <span className="font-medium text-foreground/70">
                          {ad.platform}
                        </span>
                        <span>{ad.objective}</span>
                        <span>{ad.creative_type}</span>
                      </div>

                      {/* Progress bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>
                            {format(new Date(ad.start_date), "MMM d")}
                          </span>
                          <span className="font-medium">{pct}% complete</span>
                          <span>
                            {format(new Date(ad.end_date), "MMM d, yyyy")}
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              ad.status === "live"
                                ? "bg-emerald-500"
                                : ad.status === "paused"
                                  ? "bg-amber-400"
                                  : ad.status === "completed"
                                    ? "bg-slate-400"
                                    : "bg-blue-400"
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {totalDays} day campaign
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="flex flex-wrap gap-2 items-center sm:items-start sm:shrink-0 sm:flex-col sm:items-end lg:flex-row lg:flex-nowrap">
                    {/* Status transitions */}
                    {ad.status === "planned" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(ad.id, "live")}
                        className="gap-1.5"
                      >
                        <Play className="w-3 h-3" /> Go Live
                      </Button>
                    )}
                    {ad.status === "live" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(ad.id, "paused")}
                          className="gap-1.5"
                        >
                          <Pause className="w-3 h-3" /> Pause
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(ad.id, "completed")}
                          className="gap-1.5"
                        >
                          <CheckCheck className="w-3 h-3" /> Done
                        </Button>
                      </>
                    )}
                    {ad.status === "paused" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(ad.id, "live")}
                        className="gap-1.5"
                      >
                        <Play className="w-3 h-3" /> Resume
                      </Button>
                    )}

                    {/* Edit & Delete — privileged only */}
                    {isPrivileged && (
                      <>
                        {/* Edit */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(ad)}
                          className="gap-1.5 text-muted-foreground hover:text-foreground"
                          aria-label="Edit campaign"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          <span className="sm:hidden lg:inline">Edit</span>
                        </Button>

                        {/* Delete */}
                        <AlertDialog
                          open={deletingId === ad.id}
                          onOpenChange={(open) => !open && setDeletingId(null)}
                        >
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeletingId(ad.id)}
                              className="gap-1.5 text-muted-foreground hover:text-destructive"
                              aria-label="Delete campaign"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span className="sm:hidden lg:inline">
                                Delete
                              </span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="max-w-sm w-[calc(100vw-2rem)] sm:w-full">
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete campaign?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the{" "}
                                <span className="font-medium text-foreground">
                                  {ad.client_name}
                                </span>{" "}
                                campaign on{" "}
                                <span className="font-medium text-foreground">
                                  {ad.platform}
                                </span>
                                . This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
                              <AlertDialogCancel className="mt-0">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(ad.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle>Edit Ad Campaign</DialogTitle>
            <DialogDescription>
              Update the campaign details below.
            </DialogDescription>
          </DialogHeader>
          <CampaignForm
            formData={editForm}
            setFormData={setEditForm}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
